import { lazy, type ComponentType } from 'react';

// After a deploy, a long-running display still references the old hashed chunk names. Loading a
// lazy chunk then fails (seen on the iPad: "'text/html' is not a valid JavaScript MIME type").
// Reloading picks up the new bundle; the guard stops a reload loop if the chunk is truly broken.

const KEY = 'obv-chunk-reload-at';
const WINDOW_MS = 60_000;

export function shouldReloadAfterChunkError(now: number, storage: Pick<Storage, 'getItem' | 'setItem'> | null): boolean {
  if (!storage) return false;
  try {
    const last = Number(storage.getItem(KEY) || 0);
    if (now - last < WINDOW_MS) return false;
    storage.setItem(KEY, String(now));
    return true;
  } catch {
    return false;
  }
}

function sessionStore(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

// Same constraint as React.lazy itself.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyWithReload<T extends ComponentType<any>>(factory: () => Promise<{ default: T }>) {
  return lazy(() =>
    factory().catch((err: unknown) => {
      if (shouldReloadAfterChunkError(Date.now(), sessionStore())) {
        window.location.reload();
        // Keep Suspense pending while the page reloads.
        return new Promise<{ default: T }>(() => undefined);
      }
      throw err;
    }),
  );
}
